<template>
    <div class="card return-card">
        <div class="d-flex flex-wrap align-items-center gap-3 px-2 py-2 border-bottom toolbar">
            <div class="search-box">
                <input
                    v-model="search"
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Search product..."
                >
            </div>
            <div class="form-check form-switch d-flex align-items-center gap-2 ps-0 mb-0">
                <label class="form-check-label toolbar-label" for="hide-returned-switch">Hide fully returned</label>
                <input
                    v-model="hideReturned"
                    class="form-check-input m-0"
                    type="checkbox"
                    role="switch"
                    id="hide-returned-switch"
                />
            </div>
            <span class="toolbar-hint text-muted">
                <i class="fas fa-info-circle"></i>
                Tick a row to return its full returnable qty
            </span>
            <span class="ml-auto count-pill">
                Showing {{ visibleItems.length }} of {{ tableItems.length }} &middot; {{ returnableCount }} returnable
            </span>
        </div>

        <div class="table-container">
            <table class="table create-billing table-width">
                <thead class="sticky-head">
                    <tr>
                        <th class="th-check">
                            <input type="checkbox" :checked="allVisibleSelected" :indeterminate="partiallySelected" @change="toggleSelectAll">
                        </th>
                        <th class="pl-10 particular">Product</th>
                        <th class="num">Total Qty</th>
                        <th class="num">Prev Returned</th>
                        <th class="num">Returnable Qty</th>
                        <th class="num">Avg. Rate</th>
                        <th class="th-action">Return Qty</th>
                    </tr>
                </thead>

                <tbody>
                    <tr v-for="item in visibleItems" :key="item.product_id" :class="{ 'row-selected': item.return_qty > 0, 'row-muted': item.returnable_quantity <= 0 }">
                        <td class="th-check">
                            <input type="checkbox" :checked="item.return_qty > 0" @change="toggleRow(item)" :disabled="item.returnable_quantity <= 0">
                        </td>
                        <td class="pl-10">{{ item.product_name }}</td>
                        <td class="num">{{ item.total_quantity }}</td>
                        <td class="num">{{ item.total_returned_quantity }}</td>
                        <td
                            class="num"
                            :class="{ 'returnable-cell': item.returnable_quantity > 0 }"
                            :title="returnableCellTitle(item)"
                            @click="fillMax(item)"
                        >{{ item.returnable_quantity }}</td>
                        <td class="num">
                            {{ commaFormat(item.average_rate) }}
                            <span v-if="item.has_multiple_rates" class="rate-badge" title="Bills have different rates; the return uses each bill's actual rate">varies</span>
                        </td>
                        <td>
                            <input
                                v-model.number="item.return_qty"
                                type="number"
                                class="form-control form-control-sm qty-input"
                                :class="{ 'is-invalid': isOverReturnable(item) }"
                                :min="0"
                                :max="item.returnable_quantity"
                                :disabled="item.returnable_quantity <= 0"
                            >
                            <div v-if="isOverReturnable(item)" class="text-danger small mt-1">
                                Return qty can't be greater than returnable qty
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <p v-if="!tableItems.length" class="text-center mt-5">No returnable items</p>
            <p v-if="tableItems.length && !visibleItems.length" class="text-center mt-5">No product matches "{{ search }}"</p>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import figureFormatter from '@/services/utils/figureFormatter';

const props = defineProps({
    tableItems: {
        type: Array,
        required: true
    }
})

const { commaFormat } = figureFormatter();

const search = ref('');
const hideReturned = ref(false);

const matchesSearch = (item, term) => {
    if (! term) {
        return true;
    }
    return item.product_name.toLowerCase().includes(term);
}

const matchesReturnable = (item) => {
    if (! hideReturned.value) {
        return true;
    }
    return item.returnable_quantity > 0;
}

const visibleItems = computed(() => {
    const term = search.value.trim().toLowerCase();
    return props.tableItems.filter(item => matchesSearch(item, term) && matchesReturnable(item));
})

const returnableVisibleItems = computed(() => {
    return visibleItems.value.filter(item => item.returnable_quantity > 0);
})

const returnableCount = computed(() => {
    return props.tableItems.filter(item => item.returnable_quantity > 0).length;
})

const allVisibleSelected = computed(() => {
    if (! returnableVisibleItems.value.length) {
        return false;
    }
    return returnableVisibleItems.value.every(item => item.return_qty > 0);
})

const partiallySelected = computed(() => {
    if (allVisibleSelected.value) {
        return false;
    }
    return returnableVisibleItems.value.some(item => item.return_qty > 0);
})

const isOverReturnable = (item) => {
    if (! item.return_qty) {
        return false;
    }
    return item.return_qty > item.returnable_quantity;
}

const setReturnable = (item) => {
    item.return_qty = item.returnable_quantity;
}

const returnableCellTitle = (item) => {
    if (item.returnable_quantity <= 0) {
        return '';
    }
    return 'Click to fill return qty';
}

const fillMax = (item) => {
    if (item.returnable_quantity <= 0) {
        return;
    }
    setReturnable(item);
}

const toggleRow = (item) => {
    if (item.returnable_quantity <= 0) {
        return;
    }
    if (item.return_qty > 0) {
        item.return_qty = 0;
        return;
    }
    setReturnable(item);
}

const toggleSelectAll = () => {
    if (allVisibleSelected.value) {
        returnableVisibleItems.value.forEach(item => { item.return_qty = 0; });
        return;
    }
    returnableVisibleItems.value.forEach(setReturnable);
}
</script>

<style scoped>
.return-card {
    margin-bottom: 0;
}

.particular {
    width: 30%;
}

.th-check {
    width: 40px;
    text-align: center;
}

.search-box {
    max-width: 260px;
    width: 100%;
}

.toolbar {
    row-gap: 0.5rem;
}

.toolbar-label {
    font-size: 0.8rem;
    white-space: nowrap;
    cursor: pointer;
    margin-bottom: 0;
    user-select: none;
}

.toolbar-hint {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.75rem;
    white-space: nowrap;
}

.count-pill {
    font-size: 0.75rem;
    color: #6c757d;
    background-color: rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 999px;
    padding: 0.15rem 0.6rem;
    white-space: nowrap;
}

.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
}

.row-muted td {
    opacity: 0.55;
}

.returnable-cell {
    cursor: pointer;
    text-decoration: underline dotted;
    text-underline-offset: 3px;
}

.qty-input {
    max-width: 100px;
    margin-left: auto;
    text-align: right;
}

.rate-badge {
    background-color: #fff3cd;
    color: #856404;
    border-radius: 3px;
    font-size: 0.7rem;
    padding: 0 0.3rem;
    margin-left: 0.25rem;
    cursor: help;
    white-space: nowrap;
}

.table-container {
    max-height: calc(100vh - 300px);
    overflow-y: auto;
}

.sticky-head th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: var(--bs-card-bg, #fff);
}

.create-billing tbody tr:nth-child(even) {
    background-color: rgba(0, 0, 0, 0.025);
}

.create-billing tbody tr.row-selected {
    background-color: rgba(13, 110, 253, 0.08);
}

.create-billing > :not(caption) > * > * {
    padding: 0.35rem 0.4rem;
    vertical-align: middle;
}

.create-billing .form-control-sm {
    padding: 0.15rem 0.4rem;
    height: auto;
}
</style>
